import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MoonDivider from "@/components/MoonDivider";

const KEY = "adc_customer";

export default function Account() {
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
  });
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [optIn, setOptIn] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/customers/login" : "/customers/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name, marketing_opt_in: optIn };
      const res = await api.post(path, payload);
      const data = { token: res.data.token, user: res.data.user };
      localStorage.setItem(KEY, JSON.stringify(data));
      setCustomer(data);
      window.dispatchEvent(new Event("adc-auth-change"));
      toast.success(mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY);
    setCustomer(null);
    window.dispatchEvent(new Event("adc-auth-change"));
    toast.success("Signed out");
  };

  const field = "w-full bg-noir-2 border border-white/15 px-4 py-3 font-body text-cream placeholder:text-white/30 focus:border-gold focus:outline-none transition-colors";

  if (customer?.user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="account-page">
        <User className="w-9 h-9 text-gold mx-auto mb-5" strokeWidth={1.3} />
        <h1 className="font-serif text-4xl text-cream mb-2">Welcome, {customer.user.name}</h1>
        <p className="font-body text-white/50 mb-10" data-testid="account-email">{customer.user.email}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop" className="px-8 py-4 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors" data-testid="account-shop-btn">Continue Shopping</Link>
          <button onClick={logout} data-testid="account-logout" className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white/70 font-body uppercase tracking-[0.2em] text-xs hover:border-gold/50 hover:text-gold transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="account-page">
      <div className="text-center mb-4">
        <h1 className="font-serif text-4xl sm:text-5xl text-cream">Account</h1>
      </div>
      <MoonDivider />

      <div className="flex border border-white/10 mt-10 mb-8">
        {["login", "register"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`tab-${m}`}
            className={`flex-1 py-3 font-body uppercase tracking-[0.15em] text-xs transition-colors ${mode === m ? "bg-gold text-black" : "text-white/60 hover:text-cream"}`}>
            {m === "login" ? "Sign In" : "Create Account"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="account-form">
        {mode === "register" && (
          <input placeholder="Name" value={form.name} data-testid="acc-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
        )}
        <input type="email" required placeholder="Email" value={form.email} data-testid="acc-email"
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
        <input type="password" required placeholder="Password" value={form.password} data-testid="acc-password"
          onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
        {mode === "register" && (
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="acc-optin-label">
            <input type="checkbox" checked={optIn} data-testid="acc-optin"
              onChange={(e) => setOptIn(e.target.checked)} className="accent-[#D4AF37] mt-0.5" />
            <span className="font-body text-xs text-white/55 leading-relaxed">Email me future offers, discounts & new sculpted designs.</span>
          </label>
        )}
        <button type="submit" disabled={loading} data-testid="account-submit"
          className="w-full py-4 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors disabled:opacity-50">
          {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
        </button>
      </form>

      <div className="mt-8 text-center border-t border-white/10 pt-6">
        <p className="font-body text-sm text-white/50 mb-3">No account needed to order.</p>
        <Link to="/cart" className="font-body uppercase tracking-[0.2em] text-xs text-gold border-b border-gold/40 pb-1 hover:border-gold" data-testid="guest-checkout-link">
          Continue as Guest
        </Link>
      </div>
    </div>
  );
}
